import * as React from 'react';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import { Outlet, Link, useLocation } from 'react-router-dom';
import LeftPane from './LeftPane';
import Button from '@mui/material/Button';
import ConfigModal from '../controls/ConfigModal';
import { useDispatch, useSelector } from 'react-redux';
import {
  updateConfigOpen,
  updateMarket,
  updateRegressionTestId,
  selectMarket,
  selectRegressionTestId,
} from '../../redux/app/configSlice';
import { useStytch } from '@stytch/react';
import { useCallback } from 'react';
import HomeIcon from '@mui/icons-material/Home';
import DevicesIcon from '@mui/icons-material/Devices';
import LogoutIcon from '@mui/icons-material/Logout';
import LinearScaleIcon from '@mui/icons-material/LinearScale';
import HistoryIcon from '@mui/icons-material/History';
import Typography from '@mui/material/Typography';
import DownloadFullDataButton from '../export/DownloadFullDataButton';
import IconButton from '@mui/material/IconButton';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import MenuIcon from '@mui/icons-material/Menu';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import TuneIcon from '@mui/icons-material/Tune';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import BugReportModal from '../bugreport/BugReportModal';
import { openBugReportModal } from '../../redux/bugReport/bugReportSlice';
import BugReportIcon from '@mui/icons-material/BugReport';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import RegressionIdsModal from '../regression/RegressionIdsModal';
import { useThemeMode } from '../../theme/ThemeModeContext';
import { FLAG_MAP } from '../../config/marketFlags';

const drawerWidth = 240;

function PermanentFrame() {
  const dispatch = useDispatch();
  const location = useLocation();
  const { mode, toggle: toggleThemeMode } = useThemeMode();

  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [isDevMode, setIsDevMode] = React.useState(false);

  const [isMobile, setIsMobile] = React.useState(window.innerWidth < 1200);
  const [isNavCompact, setIsNavCompact] = React.useState(window.innerWidth <= 1368);

  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1200);
      setIsNavCompact(window.innerWidth <= 1368);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  React.useEffect(() => {
    if (!isNavCompact && menuOpen) {
      setMenuOpen(false);
    }
  }, [isNavCompact, menuOpen]);

  // Shift+D x3 within 1.5s toggles developer mode
  React.useEffect(() => {
    const timestamps = [];
    const handler = (e) => {
      if (e.shiftKey && e.key === 'D') {
        const now = Date.now();
        timestamps.push(now);
        while (timestamps.length > 0 && now - timestamps[0] > 1500) {
          timestamps.shift();
        }
        if (timestamps.length >= 3) {
          timestamps.length = 0;
          setIsDevMode((prev) => !prev);
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const toggleDrawer = () => setMobileOpen(!mobileOpen);

  // Retrieve current market from localStorage/Redux
  const marketStore = useSelector(selectMarket);
  let currentMarket = marketStore;
  if (localStorage.getItem('market') !== null) {
    currentMarket = JSON.parse(localStorage.getItem('market'));
  }
  const selectedMarketFlag = currentMarket?.id ? FLAG_MAP[currentMarket.id] : null;

  // Retrieve current regression test ID from localStorage/Redux
  const regressionTestIdStore = useSelector(selectRegressionTestId);
  let currentRegressionTestId = regressionTestIdStore;
  if (localStorage.getItem('regressionTestId') !== null) {
    currentRegressionTestId = localStorage.getItem('regressionTestId');
  }

  // Initialize Redux state from localStorage on mount
  React.useEffect(() => {
    const storedMarket = localStorage.getItem('market');
    const storedRegressionTestId = localStorage.getItem('regressionTestId');

    if (storedMarket && !marketStore?.id) {
      const market = JSON.parse(storedMarket);
      if (market?.id) {
        dispatch(updateMarket(market));
      }
    }

    if (storedRegressionTestId && !regressionTestIdStore) {
      dispatch(updateRegressionTestId(storedRegressionTestId));
    }
  }, [dispatch, marketStore, regressionTestIdStore]);

  const selectedColor = 'secondary.main';
  const defaultColor = 'text.disabled';

  const ThemeToggleButton = () => (
    <Tooltip title={mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}>
      <IconButton onClick={toggleThemeMode} sx={{ color: 'text.primary' }}>
        {mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
      </IconButton>
    </Tooltip>
  );

  const LogoutButton = () => {
    const stytchClient = useStytch();
    const logout = useCallback(() => {
      stytchClient.session.revoke();
    }, [stytchClient]);
    return (
      <Button
        sx={{
          color: 'text.primary',
          fontWeight: 'bold',
          display: 'flex',
          justifyContent: 'flex-start',
          textTransform: 'none',
          px: 2,
          py: 0.75,
          fontSize: '1rem',
        }}
        onClick={logout}
      >
        <LogoutIcon fontSize="small" sx={{ color: defaultColor, mr: 1 }} />
        Logout
      </Button>
    );
  };
  // Settings Button
  const SettingsButton = ({ selectedMarketFlag, onClick, fullWidth = false }) => (
    <Button
      onClick={onClick}
      sx={{
        color: 'text.primary',
        fontWeight: 'bold',
        display: 'flex',
        justifyContent: 'flex-start',
        textTransform: 'none',
        px: 2,
        py: 0.75,
        fontSize: '1rem',
        width: fullWidth ? '100%' : 'auto',
        alignItems: 'center',
        minWidth: 0, // prevents oversized button
      }}
    >
      {selectedMarketFlag && (
        <img
          src={selectedMarketFlag}
          alt="flag"
          style={{
            width: 20,
            height: 20,
            marginRight: 8,
            objectFit: 'cover',
            borderRadius: 2,
            mixBlendMode: 'multiply',
          }}
        />
      )}
      Settings
    </Button>
  );

  if (localStorage.getItem('market') === null) {
    dispatch(updateConfigOpen(true));
  }

  const NavButton = ({ to, icon, label, onClick, active, external = false }) => {
    return (
      <Button
        component={external ? 'a' : to ? Link : 'button'}
        href={external ? to : undefined}
        to={!external ? to : undefined}
        target={external ? '_blank' : undefined}
        rel={external ? 'noopener noreferrer' : undefined}
        onClick={onClick}
        sx={{
          color: 'text.primary',
          fontWeight: 'bold',
          display: 'flex',
          alignItems: 'center',
          textTransform: 'none',
          fontSize: '1rem',
          paddingX: 1.5,
        }}
      >
        {React.cloneElement(icon, {
          fontSize: 'small',
          sx: {
            color: active ? selectedColor : defaultColor,
            mr: 1,
          },
        })}
        {label}
      </Button>
    );
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          height: '100vh',
          backgroundColor: 'background.default',
        }}
      >
        <AppBar
          position="fixed"
          sx={{
            width: isMobile ? '100%' : `calc(100% - ${drawerWidth}px)`,
            ml: isMobile ? 0 : `${drawerWidth}px`,
            backgroundColor: 'background.paper',
            color: 'text.primary',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
          }}
        >
          {location.pathname.includes('beacons') ? <DownloadFullDataButton /> : <Box />}
          {/* HAMBURGER MENU BUTTON (ONLY when width < 1368px) */}
          {isNavCompact ? (
            <Toolbar sx={{ justifyContent: 'flex-end', pr: 2 }}>
              <ThemeToggleButton />
              <IconButton onClick={() => setMenuOpen(true)}>
                <MenuIcon sx={{ color: 'text.primary' }} />
              </IconButton>
            </Toolbar>
          ) : (
            <Toolbar sx={{ justifyContent: 'flex-end', pr: 3 }}>
              <NavButton
                to="/"
                icon={<HomeIcon />}
                label="Home"
                active={location.pathname === '/'}
              />

              <SettingsButton
                selectedMarketFlag={selectedMarketFlag}
                onClick={() => dispatch(updateConfigOpen(true))}
                fullWidth={false}
              />

              <NavButton
                icon={<DevicesIcon />}
                label="Devices"
                to="/devices"
                active={location.pathname === '/devices'}
              />
              <NavButton
                icon={<LinearScaleIcon />}
                label="Flows"
                to="/flows"
                active={location.pathname === '/flows'}
              />
              <NavButton
                icon={<HistoryIcon />}
                label="Test History"
                to="/test-history"
                active={location.pathname.includes('/test-history')}
              />

              <NavButton
                to="/app-flows"
                icon={<LinearScaleIcon />}
                label="App Flows"
                active={location.pathname === '/app-flows'}
              />
              <NavButton
                to="/active-runs"
                icon={<LinearScaleIcon />}
                label="Active Runs"
                active={location.pathname === '/active-runs'}
              />
              <NavButton
                to="/locator-drift"
                icon={<ManageSearchIcon />}
                label="Locator Drift"
                active={location.pathname === '/locator-drift'}
              />
              {isDevMode && (
                <>
                  <Chip label="DEV" size="small" color="warning" sx={{ mx: 0.5 }} />
                  <NavButton
                    to="/locators"
                    icon={<TuneIcon />}
                    label="Locators"
                    active={location.pathname === '/locators'}
                  />
                </>
              )}
              <ThemeToggleButton />
              <LogoutButton />
            </Toolbar>
          )}
        </AppBar>
        <ConfigModal />
        <RegressionIdsModal />
        {/* --- LEFT PANEL/DRAWER (Single permanent drawer with CSS-based show/hide for mobile) --- */}
        <Drawer
          variant="permanent"
          anchor="left"
          sx={{
            // On mobile: don't reserve width when closed
            width: isMobile ? 0 : drawerWidth,
            flexShrink: 0,
            transition: 'width 0.3s ease-in-out',
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
              backgroundColor: 'background.paper',
              padding: isMobile ? 0 : '10px',
              borderRight: '1px solid',
              borderColor: 'divider',
              // On mobile: slide in/out based on mobileOpen state
              transform:
                isMobile && !mobileOpen ? `translateX(-${drawerWidth}px)` : 'translateX(0)',
              transition: 'transform 0.3s ease-in-out',
              // On mobile: add shadow when open, and position fixed so it overlays content
              boxShadow: isMobile && mobileOpen ? '2px 0 8px rgba(0,0,0,0.15)' : 'none',
              position: isMobile ? 'fixed' : 'relative',
              zIndex: isMobile ? 1200 : 'auto',
            },
          }}
        >
          {/* Close button - only shown on mobile */}
          {isMobile && (
            <IconButton onClick={toggleDrawer} sx={{ ml: 'auto', mt: 1 }}>
              <ChevronLeftIcon />
            </IconButton>
          )}

          <Link to="/">
            <Box sx={{ mx: 2, mt: 0, mb: 0 }}>
              <img
                style={{
                  width: '100%',
                  borderRadius: '8px',
                  marginTop: '-20px',
                  marginBottom: '-20px',
                }}
                alt="EVA Logo"
                src="/logo-with-text.png"
              />
            </Box>
          </Link>

          {/* Regression Test ID Display */}
          <Box
            sx={{
              mx: 0.5,
              mb: 2,
              p: 1.5,
              bgcolor: 'custom.surfaceAlt',
              borderRadius: 2,
              cursor: 'pointer',
              '&:hover': { bgcolor: 'action.hover' },
            }}
            onClick={() => dispatch(updateConfigOpen(true))}
          >
            <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
              Regression Test ID
            </Typography>
            <Typography variant="body2" fontWeight="bold" sx={{ color: 'primary.main' }}>
              {currentRegressionTestId || 'Not set'}
            </Typography>
          </Box>

          <LeftPane />
        </Drawer>

        {/* Backdrop overlay for mobile when drawer is open */}
        {isMobile && mobileOpen && (
          <Box
            onClick={toggleDrawer}
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 1199, // Below drawer (1200) but above content
            }}
          />
        )}
        {/* FLOATING > ARROW WHEN DRAWER IS CLOSED */}
        {isMobile && !mobileOpen && (
          <IconButton
            onClick={toggleDrawer}
            sx={{
              position: 'fixed',
              top: '50%',
              left: 12,
              zIndex: 2000,
              backgroundColor: 'background.paper',
              boxShadow: 3,
              '&:hover': { backgroundColor: 'action.hover' },
            }}
          >
            <ChevronRightIcon />
          </IconButton>
        )}
        {/* RIGHT-SIDE NAV DRAWER (Hamburger menu) */}
        <Drawer
          anchor="right"
          open={menuOpen}
          onClose={() => setMenuOpen(false)}
          sx={{
            '& .MuiDrawer-paper': {
              width: 210,
              padding: '6px 16px',
              boxSizing: 'border-box',
            },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
              alignItems: 'flex-start',
            }}
          >
            <NavButton
              to="/"
              icon={<HomeIcon />}
              label="Home"
              active={location.pathname === '/'}
              onClick={() => setMenuOpen(false)}
            />

            <SettingsButton
              selectedMarketFlag={selectedMarketFlag}
              onClick={() => {
                dispatch(updateConfigOpen(true));
                setMenuOpen(false);
              }}
              fullWidth={true}
            />

            <NavButton
              to="/devices"
              icon={<DevicesIcon />}
              label="Devices"
              active={location.pathname === '/devices'}
              onClick={() => setMenuOpen(false)}
            />

            <NavButton
              to="/flows"
              icon={<LinearScaleIcon />}
              label="Flows"
              active={location.pathname === '/flows'}
              onClick={() => setMenuOpen(false)}
            />

            <NavButton
              to="/test-history"
              icon={<HistoryIcon />}
              label="Test History"
              active={location.pathname.includes('/test-history')}
              onClick={() => setMenuOpen(false)}
            />
            <NavButton
              to="/app-flows"
              icon={<LinearScaleIcon />}
              label="App Flows"
              active={location.pathname === '/app-flows'}
            />
            <NavButton
              to="/active-runs"
              icon={<LinearScaleIcon />}
              label="Active Runs"
              active={location.pathname === '/active-runs'}
            />
            <NavButton
              to="/locator-drift"
              icon={<ManageSearchIcon />}
              label="Locator Drift"
              active={location.pathname === '/locator-drift'}
              onClick={() => setMenuOpen(false)}
            />
            {isDevMode && (
              <>
                <Chip label="DEV" size="small" color="warning" />
                <NavButton
                  to="/locators"
                  icon={<TuneIcon />}
                  label="Locators"
                  active={location.pathname === '/locators'}
                  onClick={() => setMenuOpen(false)}
                />
              </>
            )}
            <LogoutButton />
          </Box>
        </Drawer>

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            bgcolor: 'background.default',
            p: 3,
            width: '100%',
            maxWidth: '100%',
            overflowX: 'hidden',
            boxSizing: 'border-box',
          }}
        >
          <Toolbar />
          <Outlet />

          {/* BUG REPORT BUTTON */}
          <Box
            sx={{
              position: 'fixed',
              bottom: 1,
              right: 10,
              zIndex: 3000,
            }}
          >
            <Button
              variant="contained"
              sx={{
                bgcolor: 'error.main',
                color: 'error.contrastText',
                borderRadius: '30px',
                px: 2,
                py: 0.5,
                textTransform: 'none',
                fontWeight: 'bold',
                gap: 1,
                boxShadow: 3,
                '&:hover': { bgcolor: 'error.dark' },
              }}
              onClick={() => dispatch(openBugReportModal())}
            >
              <BugReportIcon fontSize="small" />
              Report Bug
            </Button>
          </Box>

          <BugReportModal />
        </Box>
      </Box>
    </>
  );
}

export default PermanentFrame;
